"use client";

import { useState, useEffect, useMemo } from "react";
import { ArrowLeft, Upload, Loader2, X, Sparkles, Download, RefreshCw, Camera, Play, Video } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { showInsufficientCreditsToast } from "@/lib/insufficient-credits";
import { useAuth } from "@/components/auth-provider";
import { useAuthModal } from "@/store/auth-modal-store";
import { useUserStore } from "@/store/user-store";
import { MediaSelector } from "@/components/media-selector";
import { cn } from "@/lib/utils";
import { createGenerationTask, fetchGenerations } from "@/lib/api";
import { motion } from "framer-motion";
import { GenerationSource } from "@/lib/constants";
import { usePricingQuote } from "@/hooks/use-pricing-quote";
import { useGenerationPolling } from "@/hooks/use-generation-polling";
import { useGenerationStore } from "@/store/generation-store";
import toolsConfig from "@ai-studio/shared/catalog/models.tools.json";
import { GenerationTimer } from "@/components/generation/generation-timer";
import { showGenerationStartedToast } from "@/lib/generation-started-toast";

// --- Constants ---

// Extract configuration from shared catalog
const babyDanceModelFamily = toolsConfig.models.find(m => m.id === "wiro-baby-dance");
const babyDanceVariant = babyDanceModelFamily?.variants[0];
const params = babyDanceVariant?.params as any;
const effectTypeParam = params?.effect_type;

// Fallback to empty arrays if config is missing (though it shouldn't be)
const EFFECT_TYPES = effectTypeParam?.options || [];

// Sample Characters
const SAMPLES = [
  {
    id: "sample1",
    label: "Sample 1",
    display: "https://cdn.wiro.ai/uploads/sampleinputs/babydanceflow-input-1.jpg",
    api: "https://cdn.wiro.ai/uploads/sampleinputs/babydanceflow-input-1.jpg"
  },
  {
    id: "sample2",
    label: "Sample 2",
    display: "https://cdn.wiro.ai/uploads/sampleinputs/babydanceflow-input-2.jpg",
    api: "https://cdn.wiro.ai/uploads/sampleinputs/babydanceflow-input-2.jpg"
  }
];

interface BabyDanceViewProps {
    isModal?: boolean;
    onClose?: () => void;
}

export function BabyDanceView({ isModal = false, onClose }: BabyDanceViewProps) {
  const { user } = useAuth();
  const openAuthModal = useAuthModal((s) => s.open);
  const { credits } = useUserStore();
  // Preset deep-link
  const pendingPreset = useGenerationStore((s) => s.pendingPreset);
  const setPendingPreset = useGenerationStore((s) => s.setPendingPreset);

  // State
  const [inputImage, setInputImage] = useState<{ display: string, api: string } | null>(null);
  const [effectType, setEffectType] = useState<string>(
    () => {
      const preset = pendingPreset;
      const fromDefaults = preset?.defaults?.effect_type as string | undefined;
      // Also try to match preset ID to an effect type
      const fromId = EFFECT_TYPES.find((e: { value: string }) => preset?.id?.includes(e.value))?.value;
      return fromDefaults || fromId || "";
    }
  );

  useEffect(() => {
    if (pendingPreset) {
      const fromDefaults = pendingPreset.defaults?.effect_type as string | undefined;
      const fromId = EFFECT_TYPES.find((e: { value: string }) => pendingPreset.id?.includes(e.value))?.value;
      if (fromDefaults || fromId) {
        setEffectType(fromDefaults || fromId!);
      }
      setPendingPreset(null);
    }
  }, [pendingPreset, setPendingPreset]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null);
  const [resultVideo, setResultVideo] = useState<string | null>(null);

  // Pricing
  const pricingPayload = useMemo(() => {
    if (!babyDanceVariant) return null;
    return { kind: 'video' as const, model_id: babyDanceVariant.id, params: { effect_type: effectType } };
  }, [effectType]);
  const { quote: priceQuote } = usePricingQuote(pricingPayload);
  const price = priceQuote?.user_price_credits ?? null;

  // --- Handlers ---

  const handleMediaSelect = (url: string) => {
    setInputImage({ display: url, api: url });
    setIsMediaSelectorOpen(false);
  };

  const handleGenerate = async () => {
    if (!user) {
      openAuthModal({ message: "Войдите, чтобы создать анимацию" });
      return;
    }
    if (!effectType) {
      toast.error("Выберите танец");
      return;
    }
    if (!inputImage) {
      toast.error("Загрузите фото");
      return;
    }
    if (price != null && credits < price) {
      showInsufficientCreditsToast();
      return;
    }

    setIsProcessing(true);
    setGenerationStartedAt(Date.now());
    setResultVideo(null);

    try {
      const task = await createGenerationTask({
        model_id: "wiro-baby-dance-v1",
        prompt: "Танцующий малыш",
        source: GenerationSource.BABY_DANCE,
        params: {
          input_image: inputImage.api,
          effect_type: effectType
        },
        user_price_credits: price ?? undefined,
      });

      setTaskId(task.id);
      showGenerationStartedToast();

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Не удалось начать генерацию";
      toast.error(message);
      setIsProcessing(false);
      setGenerationStartedAt(null);
    }
  };

  useGenerationPolling(taskId, {
    onComplete: (resultUrl) => {
      setIsProcessing(false);
      setGenerationStartedAt(null);
      setTaskId(null);
      setResultVideo(resultUrl);
      toast.success("Видео готово!");
    },
    onFailed: () => {
      setIsProcessing(false);
      setGenerationStartedAt(null);
      setTaskId(null);
      toast.error("Генерация не удалась");
    },
    onTimeout: () => {
      setIsProcessing(false);
      setGenerationStartedAt(null);
      setTaskId(null);
      toast.error("Превышено время ожидания. Попробуйте снова.");
    },
  });

  // --- Restoration Logic ---
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!user && !isInitialized) {
      setIsInitialized(true);
    }
  }, [user, isInitialized]);

  useEffect(() => {
    const loadLastGeneration = async () => {
      if (isInitialized || !user) return;
      
      try {
        const response = await fetchGenerations({
            limit: 1,
            sources: [GenerationSource.BABY_DANCE]
        });

        if (response.items && response.items.length > 0) {
            const lastGen = response.items[0];

            // Restore Effect Type (selection, not photo)
            if (lastGen.params?.effect_type) {
                setEffectType(lastGen.params.effect_type);
            }

            // Only restore active (in-progress) task, not completed results or uploaded photo
            if (lastGen.status === 'PENDING' || lastGen.status === 'PROCESSING') {
                if (lastGen.params?.input_image) {
                    setInputImage({
                        display: lastGen.params.input_image,
                        api: lastGen.params.input_image
                    });
                }
                setTaskId(lastGen.id);
                setIsProcessing(true);
                toast.info("Восстановление активной генерации...");
            }
        }
      } catch (e) {
        // Session restore failed — start fresh
      } finally {
        setIsInitialized(true);
      }
    };

    loadLastGeneration();
  }, [user, isInitialized]);

  if (!isInitialized) {
    return (
      <div className={cn("flex items-center justify-center bg-background", isModal ? "h-full" : "h-dvh")}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground text-sm animate-pulse">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col bg-background text-foreground font-sans selection:bg-cyan-500/30 overflow-hidden relative", isModal ? "h-full" : "h-dvh")}>
      
      {isModal && onClose && (
        <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose} 
            className="absolute top-3 right-3 z-50 rounded-full bg-background/60 hover:bg-background/90 border border-border/30 h-9 w-9 text-muted-foreground hover:text-foreground transition-colors"
        >
            <X className="w-4 h-4" />
        </Button>
      )}

      {/* Main Split Layout */}
      <main className="flex-1 flex flex-col lg:flex-row relative overflow-y-auto lg:overflow-hidden min-h-0">
        
        {/* Panel 02 - Photo (second on mobile, right on desktop) */}
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
            className="order-2 w-full shrink-0 lg:shrink lg:min-h-0 h-auto lg:h-full lg:w-[50%] bg-muted/5 border-b lg:border-b-0 lg:border-l border-border relative flex flex-col p-4 lg:p-6 overflow-y-auto scrollbar-hide"
        >
           <div className={cn("flex items-center justify-between mb-3 lg:mb-6 shrink-0 h-10", isModal && "pr-10 lg:pr-0")}>
             <div className="flex items-center gap-2.5">
                 {/* DashboardHeader provides mobile back-arrow. */}
                 <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">2</div>
                 <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Персонаж</h2>
             </div>
             {inputImage && !resultVideo && (
               <Button
                 variant="ghost"
                 size="sm"
                 onClick={() => setInputImage(null)}
                 disabled={isProcessing}
                 className="text-xs text-muted-foreground hover:text-destructive h-auto p-0 hover:bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 Сбросить
               </Button>
             )}
           </div>

           <div className="flex-1 relative rounded-2xl overflow-hidden bg-background border border-border/50 shadow-sm group min-h-[180px] lg:min-h-[300px] hover:border-primary/20 transition-colors">
              {isProcessing ? (
                // Processing State
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 z-20 backdrop-blur-sm">
                   <div className="relative mb-6">
                     <div className="w-20 h-20 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                     <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-primary animate-pulse" />
                   </div>
                   <p className="text-primary font-medium tracking-wide animate-pulse">Создаем танец...</p>
                </div>
              ) : null}

              {resultVideo ? (
                // Result State
                <div className="relative w-full h-full flex items-center justify-center bg-black/5">
                  <video 
                    src={resultVideo} 
                    autoPlay 
                    loop 
                    muted 
                    controls
                    className="w-full h-full object-contain max-h-[80vh]" 
                  />
                  
                  {/* Result Actions Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 via-background/60 to-transparent flex gap-3 z-10 pt-20">
                    <Button 
                      className="flex-1 font-bold h-12 rounded-xl uppercase tracking-wider shadow-lg shadow-primary/20"
                      onClick={() => window.open(resultVideo, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Скачать
                    </Button>
                    <Button 
                      variant="outline" 
                      className="bg-background/70 border-border/60 hover:bg-background h-12 rounded-xl uppercase tracking-wider backdrop-blur-sm w-12 p-0 shrink-0"
                      onClick={() => setResultVideo(null)}
                      title="Попробовать еще"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <div className="absolute top-4 left-4 z-10">
                    <Badge className="bg-primary text-primary-foreground font-bold border-0 px-3 py-1 shadow-lg shadow-primary/20">
                      РЕЗУЛЬТАТ
                    </Badge>
                  </div>
                </div>
              ) : inputImage ? (
                // Input Image Loaded State
                <div className="relative w-full h-full bg-muted/10">
                  <Image src={inputImage.display} alt="Character" fill className="object-contain opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent pointer-events-none" />
                  
                  {/* Quick Change Button */}
                  <Button 
                     variant="secondary" 
                     size="sm"
                     disabled={isProcessing}
                     onClick={() => setIsMediaSelectorOpen(true)}
                     className="absolute bottom-4 right-4 shadow-lg backdrop-blur-sm bg-background/80 hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="w-3 h-3 mr-2" />
                    Заменить фото
                  </Button>
                </div>
              ) : (
                // Empty State / Upload
                <div className="absolute inset-0 flex flex-col p-3 lg:p-8 overflow-y-auto scrollbar-hide">
                  <div
                    onClick={() => setIsMediaSelectorOpen(true)}
                    className="flex-1 min-h-[140px] lg:min-h-[240px] cursor-pointer hover:bg-muted/50 transition-all duration-300 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:shadow-lg hover:-translate-y-1 group bg-muted/5"
                  >
                    <div className="w-14 h-14 lg:w-24 lg:h-24 rounded-full bg-background border-2 border-border flex items-center justify-center mb-2 lg:mb-6 group-hover:scale-110 group-hover:border-primary/50 transition-all duration-300 shadow-sm">
                        <Camera className="w-6 h-6 lg:w-10 lg:h-10 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <h3 className="text-base lg:text-2xl font-bold text-foreground mb-1 lg:mb-3 group-hover:text-primary transition-colors">Загрузить фото</h3>
                    <p className="text-muted-foreground text-xs lg:text-sm max-w-xs text-center leading-relaxed px-3">
                        Персонаж в полный рост
                    </p>
                  </div>
                </div>
              )}
           </div>
        </motion.div>

        {/* Panel 01 - Effect Selection (first on mobile, left on desktop) */}
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="order-1 w-full shrink-0 lg:shrink lg:min-h-0 h-[45dvh] lg:h-full lg:w-[50%] flex flex-col bg-background relative z-10"
        >
           {/* Header */}
           <div className="px-4 lg:px-6 pt-4 lg:pt-6 pb-2 bg-background z-20 shrink-0 border-b border-transparent">
              <div className="flex items-center gap-2.5 shrink-0 h-10">
                 <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">1</div>
                 <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Выберите танец</h2>
              </div>
           </div>

           {/* Effects Grid / Selected Preview */}
           <div className="flex-1 overflow-y-auto px-4 lg:px-6 pb-4 lg:pb-6 scrollbar-hide">
                {effectType ? (() => {
                   const selected = EFFECT_TYPES.find((e: { value: string }) => e.value === effectType);
                   return (
                      <div className="relative aspect-[9/16] w-full max-w-[140px] lg:max-w-[320px] mx-auto rounded-2xl overflow-hidden border border-border bg-black/5 group mt-2">
                         <video
                            src={selected?.preview}
                            className="w-full h-full object-cover"
                            autoPlay
                            muted
                            loop
                            playsInline
                         />
                         <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                         <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-md rounded text-[11px] text-white font-bold uppercase tracking-wider">
                            {selected?.label}
                         </div>
                         <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => !isProcessing && setEffectType("")}
                            disabled={isProcessing}
                            className="absolute bottom-3 right-3 shadow-lg backdrop-blur-sm bg-background/80 hover:bg-background"
                         >
                            <RefreshCw className="w-3 h-3 mr-2" />
                            Заменить
                         </Button>
                      </div>
                   );
                })() : (
                  <div className="grid grid-cols-3 gap-2 lg:gap-3 mt-2 lg:mt-0">
                     {EFFECT_TYPES.map((effect: { value: string; label: string; preview?: string }) => (
                        <div
                          key={effect.value}
                          onClick={() => !isProcessing && setEffectType(effect.value)}
                          className={cn(
                            "aspect-[3/4] lg:aspect-[9/16] rounded-xl relative overflow-hidden border-2 transition-all group flex items-center justify-center bg-muted/10",
                            isProcessing
                              ? "opacity-50 cursor-not-allowed grayscale border-transparent"
                              : "cursor-pointer border-transparent hover:border-primary/50"
                          )}
                        >
                          <div className="absolute inset-0 bg-muted/20">
                             <video
                               src={effect.preview}
                               className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                               autoPlay
                               muted
                               loop
                               playsInline
                             />
                          </div>

                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-3 pointer-events-none">
                            <p className="text-white text-xs font-bold uppercase tracking-wider">{effect.label}</p>
                          </div>
                        </div>
                     ))}
                  </div>
                )}
           </div>
        </motion.div>
      </main>

      {/* Shared Footer - Generate Button */}
      <div className="border-t border-border bg-background/80 backdrop-blur-sm z-20 shrink-0 px-4 pt-2 pb-[calc(64px+env(safe-area-inset-bottom))] md:p-4 lg:p-6 lg:pb-6">
        <Button
            onClick={handleGenerate}
            disabled={!inputImage || !effectType || isProcessing || price == null}
            className={cn(
            "w-full h-12 lg:h-14 text-sm lg:text-base font-bold uppercase tracking-widest shadow-xl transition-all duration-500",
            (!inputImage || !effectType)
                ? "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                : "bg-primary hover:bg-primary/90 text-primary-foreground hover:scale-[1.02] hover:shadow-primary/25"
            )}
        >
            {isProcessing ? (
            <GenerationTimer startedAt={generationStartedAt} label="Создаём танец" />
            ) : (
            <span className="flex items-center gap-2">
                <Play className="w-5 h-5 fill-current" />
                Создать видео {user && price != null ? `• ${price}` : ''}
            </span>
            )}
        </Button>
        {!isProcessing && (!inputImage || !effectType) && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {!inputImage ? 'Загрузите фото ребёнка, чтобы продолжить' : 'Выберите танец или движение'}
          </p>
        )}
      </div>


      <MediaSelector
        open={isMediaSelectorOpen}
        onOpenChange={setIsMediaSelectorOpen}
        onSelect={handleMediaSelect}
        maxFiles={1}
      />
    </div>
  )
}
